import React, { useEffect, useState } from 'react';
import { CheckIcon, MagnifyingGlassIcon, PlusIcon } from '@heroicons/react/24/outline';
import { drillsAPI } from '../../services/api';
import { DRILL_CATEGORIES, Drill } from '../../types/drill';
import DrillSheet from './DrillSheet';
import DrillThumbnail from './DrillThumbnail';

interface DrillPickerProps {
  onClose: () => void;
  /** Egy gyakorlat beemelése; a választó nyitva marad, így többet is fel lehet venni. */
  onPick: (drill: Drill) => void;
}

/** Gyakorlat kiválasztása a tárból az edzéstervhez. */
const DrillPicker: React.FC<DrillPickerProps> = ({ onClose, onPick }) => {
  const [drills, setDrills] = useState<Drill[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState('');
  const [added, setAdded] = useState<Record<number, number>>({});

  useEffect(() => {
    // Gépelés közben ne kérdezzünk le minden billentyűre
    const timer = setTimeout(async () => {
      setLoading(true);
      try {
        setDrills(await drillsAPI.getAll({ search: search || undefined, category: category || undefined }));
      } catch (err) {
        console.error('A gyakorlattár betöltése nem sikerült:', err);
      } finally {
        setLoading(false);
      }
    }, 250);
    return () => clearTimeout(timer);
  }, [search, category]);

  const handlePick = (drill: Drill) => {
    onPick(drill);
    setAdded((prev) => ({ ...prev, [drill.id]: (prev[drill.id] ?? 0) + 1 }));
  };

  const totalAdded = Object.values(added).reduce((a, b) => a + b, 0);

  return (
    <DrillSheet
      title="Gyakorlat a gyakorlattárból"
      onClose={onClose}
      maxWidthClass="md:max-w-3xl"
      footer={
        <div className="flex items-center justify-between gap-3">
          <p className="text-sm text-gray-500">
            {totalAdded > 0 ? `${totalAdded} gyakorlat hozzáadva az edzéstervhez.` : 'A gyakorlat másolatként kerül az edzésbe.'}
          </p>
          <button type="button" onClick={onClose} className="px-4 py-2 rounded-md bg-primary-600 text-sm font-semibold text-white hover:bg-primary-700">
            Kész
          </button>
        </div>
      }
    >
      <div className="space-y-4">
        <div className="relative">
          <MagnifyingGlassIcon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
          <input
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Keresés név vagy leírás alapján..."
            aria-label="Keresés a gyakorlattárban"
            className="w-full rounded-md border border-gray-300 py-2 pl-9 pr-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
          />
        </div>

        <div className="flex gap-2 overflow-x-auto pb-1">
          {['', ...DRILL_CATEGORIES].map((c) => (
            <button
              key={c || 'all'}
              type="button"
              onClick={() => setCategory(c)}
              aria-pressed={category === c}
              className={`shrink-0 rounded-full border px-3 py-1 text-sm ${
                category === c ? 'border-primary-600 bg-primary-600 text-white' : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50'
              }`}
            >
              {c || 'Mind'}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="py-10 text-center text-sm text-gray-500">Betöltés...</div>
        ) : drills.length === 0 ? (
          <div className="py-10 text-center text-sm text-gray-500">
            Nincs találat. Új gyakorlatot a Gyakorlattár oldalon vehetsz fel, vagy egy edzésterv-blokkból menthetsz.
          </div>
        ) : (
          <ul className="space-y-2">
            {drills.map((drill) => (
              <li key={drill.id} className="flex items-center gap-3 rounded-md border border-gray-200 p-2">
                <DrillThumbnail diagram={drill.diagram} title={drill.title} className="w-28 shrink-0 sm:w-36" />
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-medium text-gray-900">{drill.title}</p>
                  <p className="text-xs text-gray-500">
                    {[drill.category, drill.durationMinutes ? `${drill.durationMinutes} perc` : null, drill.players]
                      .filter(Boolean)
                      .join(' · ')}
                  </p>
                  {drill.description && <p className="mt-0.5 line-clamp-2 text-xs text-gray-500">{drill.description}</p>}
                </div>
                <button
                  type="button"
                  onClick={() => handlePick(drill)}
                  className={`inline-flex shrink-0 items-center gap-1 rounded-md px-3 py-2 text-sm font-medium ${
                    added[drill.id] ? 'bg-green-50 text-green-700 ring-1 ring-green-200' : 'bg-primary-600 text-white hover:bg-primary-700'
                  }`}
                >
                  {added[drill.id] ? <CheckIcon className="h-4 w-4" /> : <PlusIcon className="h-4 w-4" />}
                  {added[drill.id] ? 'Hozzáadva' : 'Hozzáadás'}
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </DrillSheet>
  );
};

export default DrillPicker;
